/**
 * Broadcast feed controller: fetch + socket + delay buffer + layout
 * versioning + call-out queue. Everything shown on air derives from here.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  fetchBroadcastState,
  fetchControlState,
  isLinkGoneError,
  type BroadcastLayout,
  type BroadcastState,
  type SceneTransition,
} from "../../lib/broadcastApi";
import type { MatchEvent } from "../../store/useMatchLogStore";
import type { Match } from "../logger/types";
import {
  buildCallout,
  calloutReducer,
  INITIAL_CALLOUT_STATE,
} from "./callouts";
import { DelayBuffer, eventKey } from "./delayBuffer";
import { shouldApplyLayout, shouldApplySeq } from "./layoutVersion";
import { mapApiEvent, mapApiMatch } from "./mappers";
import {
  useBroadcastSocket,
  type BroadcastSocketState,
} from "./useBroadcastSocket";

const RECONCILE_MS = 30_000;
/** Minimum on-air time for a late call-out that waited in the queue. */
const LATE_CALLOUT_MIN_VISIBLE_MS = 2000;

export type FeedStatus = "loading" | "ready" | "unavailable" | "off";

export interface BroadcastFeed {
  status: FeedStatus;
  layout: BroadcastLayout | null;
  broadcaster: BroadcastState["broadcaster"] | null;
  match: Match | null;
  /** Released events only: score, clock and widgets compute from these. */
  events: MatchEvent[];
  callout: ReturnType<typeof calloutReducer>;
  socketState: BroadcastSocketState;
  /** Playback "now" (server clock minus the data delay), in epoch ms. */
  getPlaybackNowMs: () => number;
  /** Replace the layout from the control page's own PUT response. */
  applyLayout: (layout: BroadcastLayout, layoutSeq?: number) => void;
  /** asset_id -> url for the assets the layout references (W3). */
  assets: Record<string, string>;
  /** Re-fetch the state now (control page, after a scene action). */
  refresh: () => Promise<void>;
  /** Latest Take: `key` changes on every applied Take (scene-root key). */
  transition: {
    key: number;
    type: SceneTransition["type"];
    durationMs: number;
  };
}

export function useBroadcastFeed(
  token: string,
  opts: {
    control?: boolean;
    socket?: boolean;
    pollMs?: number;
    /** Stop the reconcile poll (hidden tab, idle producer). */
    paused?: boolean;
    /** Control page: called for scene_updated / take / on_air socket frames. */
    onControlMessage?: (type: "scene_updated" | "take" | "on_air") => void;
    onState?: (state: BroadcastState) => void;
    onFetchError?: (err: unknown) => void;
  } = {},
): BroadcastFeed {
  const {
    control = false,
    socket = true,
    pollMs = RECONCILE_MS,
    paused = false,
  } = opts;
  const onStateRef = useRef(opts.onState);
  onStateRef.current = opts.onState;
  const onFetchErrorRef = useRef(opts.onFetchError);
  onFetchErrorRef.current = opts.onFetchError;
  const [status, setStatus] = useState<FeedStatus>("loading");
  const terminal = status === "unavailable";
  const [layout, setLayout] = useState<BroadcastLayout | null>(null);
  const [broadcaster, setBroadcaster] = useState<
    BroadcastState["broadcaster"] | null
  >(null);
  const [assets, setAssets] = useState<Record<string, string>>({});
  const [match, setMatch] = useState<Match | null>(null);
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [callout, dispatchCallout] = useReducer(
    calloutReducer,
    INITIAL_CALLOUT_STATE,
  );

  const layoutRef = useRef<BroadcastLayout | null>(null);
  const matchRef = useRef<Match | null>(null);
  const matchJsonRef = useRef<string>("");
  const matchSeq = useRef(0);
  const matchTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const bufferRef = useRef<DelayBuffer | null>(null);
  const firstLoad = useRef(true);
  const initialFetchRef = useRef<Promise<void> | null>(null);
  const timedIds = useRef<Set<string>>(new Set());
  const droppedIdRef = useRef<string | null>(null);
  const deadlineRef = useRef<{ id: string; at: number } | null>(null);

  const layoutSeqRef = useRef<number | null>(null);
  const assetsRef = useRef<Record<string, string>>({});
  const assetFetchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [transition, setTransition] = useState<BroadcastFeed["transition"]>({
    key: 0,
    type: "cut",
    durationMs: 0,
  });
  const onControlMessageRef = useRef(opts.onControlMessage);
  onControlMessageRef.current = opts.onControlMessage;

  const applyLayout = useCallback(
    (
      incoming: BroadcastLayout,
      layoutSeq?: number,
      take?: SceneTransition | null,
    ) => {
      if (typeof layoutSeq === "number") {
        if (!shouldApplySeq(layoutSeqRef.current, layoutSeq)) return;
        layoutSeqRef.current = layoutSeq;
        if (take) {
          setTransition((prev) => ({
            key: prev.key + 1,
            type: take.type,
            durationMs: take.duration_ms,
          }));
        }
      } else if (!shouldApplyLayout(layoutRef.current, incoming)) {
        return;
      }
      layoutRef.current = incoming;
      setLayout(incoming);
    },
    [],
  );

  /** Frames carry no assets map: refetch state once (coalesced) when an
   * applied frame references an asset id we cannot resolve yet. */
  const maybeFetchAssets = useCallback((l: BroadcastLayout) => {
    const ids = new Set<string>();
    const add = (v: unknown) => {
      if (typeof v === "string" && v) ids.add(v);
    };
    add(l.stage?.background_asset_id);
    const widgets = (l as unknown as { widgets?: unknown }).widgets;
    if (Array.isArray(widgets)) {
      widgets.forEach((w) => {
        const o = (w as { options?: { asset_id?: unknown } }).options;
        add(o?.asset_id);
        add((w as { panel?: { asset_id?: unknown } }).panel?.asset_id);
      });
    }
    const known = assetsRef.current;
    if (![...ids].some((id) => !(id in known))) return;
    if (assetFetchRef.current) return;
    assetFetchRef.current = setTimeout(() => {
      assetFetchRef.current = null;
      void refreshRef.current();
    }, 150);
  }, []);
  const refreshRef = useRef<() => Promise<void>>(async () => {});

  if (!bufferRef.current) {
    bufferRef.current = new DelayBuffer({
      getDelayMs: () => layoutRef.current?.stage.data_delay_ms ?? 0,
      onRelease: (event, info) => {
        setEvents((prev) =>
          prev.some((e) => eventKey(e) === eventKey(event))
            ? prev
            : [...prev, event],
        );
        const layoutNow = layoutRef.current;
        if (layoutNow) {
          // Snapshot-only events call out too, but only while recent: the
          // delayed release is still inside the type's duration.
          const c = buildCallout(event, layoutNow, info.ageMs);
          if (c) {
            const dur = layoutNow.callouts?.[c.kind]?.duration_ms ?? 5000;
            if (info.allowCallout || info.ageMs < dur)
              dispatchCallout({ type: "enqueue", callout: c });
          }
        }
      },
      onWithdraw: (id) => {
        setEvents((prev) => prev.filter((e) => eventKey(e) !== id));
        dispatchCallout({ type: "withdraw", id });
      },
    });
  }
  const buffer = bufferRef.current;

  const scheduleMatch = useCallback((mapped: Match, immediate: boolean) => {
    const json = JSON.stringify(mapped);
    if (json === matchJsonRef.current) return;
    matchJsonRef.current = json;
    const seq = ++matchSeq.current;
    const apply = () => {
      if (seq < matchSeq.current) return;
      matchRef.current = mapped;
      setMatch(mapped);
    };
    const delay = layoutRef.current?.stage.data_delay_ms ?? 0;
    if (immediate || delay <= 0) {
      apply();
      return;
    }
    const t = setTimeout(() => {
      matchTimers.current.delete(t);
      apply();
    }, delay);
    matchTimers.current.add(t);
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const state = control
        ? await fetchControlState(token)
        : await fetchBroadcastState(token);
      if (!control && state.on_air === false) {
        // Link is Off: nothing to render, reconcile or connect to.
        setStatus("off");
        return;
      }
      buffer.setServerTime(state.server_time);
      onStateRef.current?.(state);
      applyLayout(state.layout, state.layout_seq);
      setBroadcaster(state.broadcaster);
      assetsRef.current = state.assets ?? {};
      setAssets((prev) =>
        JSON.stringify(prev) === JSON.stringify(state.assets ?? {})
          ? prev
          : (state.assets ?? {}),
      );
      const mappedEvents = (state.events ?? []).map((raw) => ({
        mapped: mapApiEvent(raw),
        receivedAt: (raw as { received_at?: string }).received_at,
      }));
      mappedEvents.forEach(({ mapped, receivedAt }) =>
        buffer.ingest(mapped, "snapshot", receivedAt),
      );
      buffer.reconcileAbsent(
        new Set(
          mappedEvents.flatMap(({ mapped }) =>
            [mapped._id, mapped.client_id].filter(Boolean).map(String),
          ),
        ),
        state.server_time,
      );
      scheduleMatch(mapApiMatch(state.match), firstLoad.current);
      firstLoad.current = false;
      setStatus("ready");
    } catch (err) {
      onFetchErrorRef.current?.(err);
      // Any 4xx (except timeout / rate limit) means the link is not
      // available: bad, revoked, disabled or abandoned.
      if (isLinkGoneError(err)) setStatus("unavailable");
      // Any other failure: never blank. Keep what is on screen; the 30 s
      // reconcile and the socket reconnect retry.
    }
  }, [token, control, buffer, applyLayout, scheduleMatch]);

  refreshRef.current = refresh;

  useEffect(
    () => () => {
      if (assetFetchRef.current) clearTimeout(assetFetchRef.current);
    },
    [],
  );

  useEffect(() => {
    firstLoad.current = true;
    // A 4xx on the state fetch or a 1008 socket close is terminal: stop
    // polling (EPIC-109 c13); network errors and 5xx keep polling.
    if (paused || terminal) return;
    // StrictMode (dev) runs this effect twice on mount: share one in-flight
    // first fetch so a bogus token still costs exactly one request.
    if (!initialFetchRef.current) {
      initialFetchRef.current = refresh().finally(() => {
        initialFetchRef.current = null;
      });
    }
    const id = setInterval(refresh, pollMs);
    return () => clearInterval(id);
  }, [refresh, pollMs, paused, terminal]);

  useEffect(() => {
    const timers = matchTimers.current;
    return () => {
      buffer.dispose();
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [buffer]);

  const { state: socketState } = useBroadcastSocket(
    token,
    {
      onConfirmedEvent: (raw) => {
        // Events from the live socket are "just received".
        buffer.ingest(mapApiEvent(raw), "live", null);
      },
      onUndo: (id) => {
        buffer.undo(id);
      },
      onMatchUpdate: () => {
        void refresh();
      },
      onLayout: (l, seq) => {
        applyLayout(l, seq);
        if (layoutRef.current === l) maybeFetchAssets(l);
      },
      onTake: (l, seq, tr) => {
        applyLayout(l, seq, tr ?? null);
        if (layoutRef.current === l) maybeFetchAssets(l);
      },
      onControlMessage: (type) => {
        onControlMessageRef.current?.(type);
        void refresh();
      },
      onCallout: (action) => dispatchCallout({ type: action }),
      onConnected: () => {
        void refresh();
      },
    },
    socket &&
      !paused &&
      status !== "unavailable" &&
      status !== "off" &&
      // Control: never open the socket before the state fetch proved the
      // token (a bogus token costs exactly one request).
      (!control || status === "ready"),
    control ? "/ws/broadcast/control" : "/ws/broadcast",
  );

  useEffect(() => {
    if (socketState === "invalid_link") setStatus("unavailable");
    else if (socketState === "off") setStatus("off");
  }, [socketState]);

  // Auto-expire the current call-out. The window is computed when it starts
  // SHOWING, not when it was released: a late call-out gets
  // duration - ageAtRelease (floored at LATE_CALLOUT_MIN_VISIBLE_MS) and a
  // fully expired one is dropped. The deadline is kept per call-out id so a
  // layout change mid-display never resets the timer to the full duration.
  const currentCallout = callout.current;
  const currentFull = currentCallout
    ? (layout?.callouts?.[currentCallout.kind]?.duration_ms ?? 5000)
    : 0;
  const dropped =
    !!currentCallout &&
    (currentCallout.ageMs ?? 0) >= currentFull &&
    (!timedIds.current.has(currentCallout.id) ||
      droppedIdRef.current === currentCallout.id);
  useEffect(() => {
    const current = callout.current;
    if (!current) {
      deadlineRef.current = null;
      droppedIdRef.current = null;
      return;
    }
    const full = layout?.callouts?.[current.kind]?.duration_ms ?? 5000;
    let ms: number;
    if (deadlineRef.current?.id === current.id) {
      ms = Math.max(0, deadlineRef.current.at - Date.now());
    } else {
      const late = !timedIds.current.has(current.id);
      timedIds.current.add(current.id);
      const age = current.ageMs ?? 0;
      if (late && age > 0) {
        ms =
          age >= full
            ? ((droppedIdRef.current = current.id), 0)
            : Math.min(full, Math.max(LATE_CALLOUT_MIN_VISIBLE_MS, full - age));
      } else {
        ms = full;
      }
      deadlineRef.current = { id: current.id, at: Date.now() + ms };
    }
    const t = setTimeout(
      () => dispatchCallout({ type: "expire", id: current.id }),
      ms,
    );
    return () => clearTimeout(t);
  }, [callout.current, layout]);

  const visibleCallout = useMemo(
    () => (dropped ? { ...callout, current: null } : callout),
    [dropped, callout],
  );

  const getPlaybackNowMs = useCallback(
    () =>
      Date.now() +
      buffer.getOffsetMs() -
      (layoutRef.current?.stage.data_delay_ms ?? 0),
    [buffer],
  );

  return useMemo(
    () => ({
      status,
      layout,
      broadcaster,
      match,
      events,
      callout: visibleCallout,
      socketState,
      getPlaybackNowMs,
      applyLayout,
      refresh,
      assets,
      transition,
    }),
    [
      status,
      layout,
      broadcaster,
      match,
      events,
      visibleCallout,
      socketState,
      getPlaybackNowMs,
      applyLayout,
      refresh,
      assets,
      transition,
    ],
  );
}
